/**
 * Analytics Service
 *
 * Provides privacy-respecting instrumentation for the Vizi PWA.
 * Writes to InstantDB for real-time analytics data.
 *
 * Features:
 * - Session tracking with device fingerprinting (no PII)
 * - Page view tracking with route paths
 * - Error tracking with sanitized stack traces
 * - Respects user's analyticsEnabled setting
 */

import { db, id, tx } from "../instantdb";
import { settingsStore } from "../stores/settings";

// ============ Types ============

export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  deviceType: "mobile" | "tablet" | "desktop";
  platform: "iOS" | "Android" | "macOS" | "Windows" | "Linux" | "Web";
  osVersion: string;
  screenWidth: number;
  screenHeight: number;
}

interface SessionState {
  sessionId: string;
  userId: string;
  startedAt: number;
  currentRoute: string | null;
  lastRouteTimestamp: number | null;
  pageCount: number;
  errorCount: number;
  dbRecordId: string | null;
}

// ============ Module State ============

let sessionState: SessionState | null = null;

// ============ Device Detection ============

/**
 * Detects device information from the user agent and screen
 */
export function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;

  // Browser detection
  let browser = "Other";
  let browserVersion = "";
  if (ua.includes("Chrome") && !ua.includes("Edg")) {
    browser = "Chrome";
    browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] ?? "";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    browser = "Safari";
    browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] ?? "";
  } else if (ua.includes("Firefox")) {
    browser = "Firefox";
    browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] ?? "";
  } else if (ua.includes("Edg")) {
    browser = "Edge";
    browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] ?? "";
  }

  // Device type detection
  let deviceType: DeviceInfo["deviceType"] = "desktop";
  if (/iPhone|iPod|Android.*Mobile|webOS|BlackBerry/i.test(ua)) {
    deviceType = "mobile";
  } else if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) {
    deviceType = "tablet";
  }

  // Platform detection
  let platform: DeviceInfo["platform"] = "Web";
  let osVersion = "";
  if (/iPhone|iPad|iPod/.test(ua)) {
    platform = "iOS";
    osVersion = ua.match(/OS (\d+_\d+)/)?.[1]?.replace("_", ".") ?? "";
  } else if (/Android/.test(ua)) {
    platform = "Android";
    osVersion = ua.match(/Android (\d+\.?\d*)/)?.[1] ?? "";
  } else if (/Macintosh/.test(ua)) {
    platform = "macOS";
    osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace("_", ".") ?? "";
  } else if (/Windows/.test(ua)) {
    platform = "Windows";
    osVersion = ua.match(/Windows NT (\d+\.\d+)/)?.[1] ?? "";
  } else if (/Linux/.test(ua)) {
    platform = "Linux";
  }

  return {
    browser,
    browserVersion,
    deviceType,
    platform,
    osVersion,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
}

// ============ Privacy Check ============

function isAnalyticsEnabled(): boolean {
  return settingsStore.analyticsEnabled;
}

// ============ Anonymous User ID ============

const ANON_USER_KEY = "vizi-analytics-anon-id";

function getAnonymousUserId(): string {
  let anonId = localStorage.getItem(ANON_USER_KEY);
  if (!anonId) {
    anonId = `anon_${crypto.randomUUID()}`;
    localStorage.setItem(ANON_USER_KEY, anonId);
  }
  return anonId;
}

// ============ Session Management ============

/**
 * Starts a new analytics session
 * @param userId - Optional user ID (uses anonymous ID if not provided)
 */
export async function startSession(userId?: string): Promise<void> {
  if (!isAnalyticsEnabled()) return;
  if (sessionState) return; // Already have a session

  const device = detectDevice();
  const sessionId = crypto.randomUUID();
  const now = Date.now();
  const resolvedUserId = userId ?? getAnonymousUserId();
  const recordId = id();

  sessionState = {
    sessionId,
    userId: resolvedUserId,
    startedAt: now,
    currentRoute: null,
    lastRouteTimestamp: null,
    pageCount: 0,
    errorCount: 0,
    dbRecordId: recordId,
  };

  try {
    // Write session to InstantDB
    await db.transact(
      tx.analytics_sessions[recordId].update({
        userId: resolvedUserId,
        sessionId,
        ...device,
        appVersion: __APP_VERSION__,
        buildDate: __BUILD_DATE__,
        buildTimestamp: __BUILD_TIMESTAMP__,
        startedAt: now,
        pageCount: 0,
        errorCount: 0,
      }),
    );
  } catch (error) {
    console.error("[Analytics] Failed to start session:", error);
    sessionState = null;
  }
}

/**
 * Ends the current analytics session
 */
export async function endSession(): Promise<void> {
  if (!sessionState?.dbRecordId) return;

  const now = Date.now();
  const duration = Math.floor((now - sessionState.startedAt) / 1000);
  const recordId = sessionState.dbRecordId;
  const pageCount = sessionState.pageCount;
  const errorCount = sessionState.errorCount;

  // Clear state first to prevent duplicate calls
  sessionState = null;

  try {
    await db.transact(
      tx.analytics_sessions[recordId].update({
        endedAt: now,
        durationSeconds: duration,
        pageCount,
        errorCount,
      }),
    );
  } catch (error) {
    console.error("[Analytics] Failed to end session:", error);
  }
}

/**
 * Updates the session user ID (e.g., when user logs in)
 */
export async function updateSessionUserId(userId: string): Promise<void> {
  if (!sessionState?.dbRecordId) return;

  sessionState.userId = userId;

  try {
    await db.transact(
      tx.analytics_sessions[sessionState.dbRecordId].update({
        userId,
      }),
    );
  } catch (error) {
    console.error("[Analytics] Failed to update session user ID:", error);
  }
}

// ============ Page View Tracking ============

/**
 * Tracks a page view
 * @param routePath - The route path being viewed
 */
export async function trackPageView(routePath: string): Promise<void> {
  if (!isAnalyticsEnabled() || !sessionState) return;

  const now = Date.now();
  const eventId = id();

  // Calculate time on previous page
  let timeOnPrevious: number | undefined;
  if (sessionState.lastRouteTimestamp && sessionState.currentRoute) {
    timeOnPrevious = Math.floor((now - sessionState.lastRouteTimestamp) / 1000);
  }

  const previousRoute = sessionState.currentRoute;
  sessionState.currentRoute = routePath;
  sessionState.lastRouteTimestamp = now;
  sessionState.pageCount++;

  try {
    // Write event to InstantDB
    await db.transact(
      tx.analytics_events[eventId]
        .update({
          sessionId: sessionState.sessionId,
          userId: sessionState.userId,
          eventType: "page_view",
          eventName: routePath,
          timestamp: now,
          routePath,
          previousRoutePath: previousRoute ?? undefined,
          timeOnPreviousPageSeconds: timeOnPrevious,
        })
        .link({ session: sessionState.dbRecordId! }),
    );

    // Update session page count
    if (sessionState.dbRecordId) {
      await db.transact(
        tx.analytics_sessions[sessionState.dbRecordId].update({
          pageCount: sessionState.pageCount,
        }),
      );
    }
  } catch (error) {
    console.error("[Analytics] Failed to track page view:", error);
  }
}

// ============ Error Tracking ============

/**
 * Tracks an error
 * @param error - The error to track
 * @param componentStack - Optional React component stack
 */
export async function trackError(
  error: Error,
  componentStack?: string,
): Promise<void> {
  if (!isAnalyticsEnabled() || !sessionState) return;

  const now = Date.now();
  const eventId = id();
  sessionState.errorCount++;

  // Sanitize error stack (remove file paths that might contain user info)
  const sanitizedStack = error.stack?.replace(/file:\/\/[^\s]+/g, "[local]");

  try {
    await db.transact(
      tx.analytics_events[eventId]
        .update({
          sessionId: sessionState.sessionId,
          userId: sessionState.userId,
          eventType: "error",
          eventName: error.name || "Error",
          timestamp: now,
          errorMessage: error.message,
          errorStack: sanitizedStack?.slice(0, 4000), // Limit size
          componentStack: componentStack?.slice(0, 2000), // Limit size
        })
        .link({ session: sessionState.dbRecordId! }),
    );

    // Update session error count
    if (sessionState.dbRecordId) {
      await db.transact(
        tx.analytics_sessions[sessionState.dbRecordId].update({
          errorCount: sessionState.errorCount,
        }),
      );
    }
  } catch (err) {
    console.error("[Analytics] Failed to track error:", err);
  }
}

// ============ Custom Events ============

/**
 * Tracks a custom event
 * @param eventName - Name of the event
 * @param eventData - Optional event data
 */
export async function trackEvent(
  eventName: string,
  eventData?: Record<string, unknown>,
): Promise<void> {
  if (!isAnalyticsEnabled() || !sessionState) return;

  const eventId = id();

  try {
    await db.transact(
      tx.analytics_events[eventId]
        .update({
          sessionId: sessionState.sessionId,
          userId: sessionState.userId,
          eventType: "custom",
          eventName,
          eventData,
          timestamp: Date.now(),
        })
        .link({ session: sessionState.dbRecordId! }),
    );
  } catch (error) {
    console.error("[Analytics] Failed to track custom event:", error);
  }
}

// ============ Lifecycle Helpers ============

/**
 * Checks if a session is currently active
 */
export function isSessionActive(): boolean {
  return sessionState !== null;
}

/**
 * Gets the current session ID
 */
export function getSessionId(): string | null {
  return sessionState?.sessionId ?? null;
}

/**
 * Gets the current session's database record ID
 */
export function getSessionDbId(): string | null {
  return sessionState?.dbRecordId ?? null;
}
